import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { AttachmentPicker, type Attachment } from '../components/AttachmentPicker';
import { AttachmentsPanel } from '../components/AttachmentsPanel';
import { ConfirmModal } from '../components/Modal';
import { useToast } from '../components/ToastContext';
import {
  POStatus,
  formatDate,
  formatMoney,
  paymentStatusColors,
  paymentStatusLabels,
  statusColors,
  statusLabels,
} from '../lib/po-ui';

const API = import.meta.env.VITE_API_URL || '/api';

interface POItem {
  id: string;
  /** Toda linea de una orden es un producto del stock. */
  productId: string;
  productName: string;
  /** Lo que decía el documento, tal como se leyó. Trazabilidad de la lectura. */
  description?: string | null;
  /** Código de barras leído del documento, si lo traía. */
  barcode?: string | null;
  productSku?: string | null;
  productUnit?: string | null;
  productPurchaseUnit?: string | null;
  quantity: string;
  unitCost: string;
  discountPct: string;
  taxRate: string;
  receivedQuantity: string;
  subtotal: string;
  taxAmount: string;
  total: string;
}

interface Reception {
  id: string;
  receptionNumber: string;
  supplierRemitoNumber?: string | null;
  notes?: string | null;
  receptionDate: string;
}

interface Invoice {
  id: string;
  invoiceType: string;
  invoiceNumber: string;
  cai?: string | null;
  invoiceDate: string;
  taxableBase: string;
  ivaAmount: string;
  total: string;
}

interface Payment {
  id: string;
  paymentDate: string;
  paymentMethod: string;
  reference?: string | null;
  amount: string;
  totalRetentions: string;
  grossAmount: string;
}

interface PurchaseOrder {
  id: string;
  poNumber: string;
  supplierId: string;
  supplierName: string;
  status: POStatus;
  paymentStatus: 'unpaid' | 'partial_paid' | 'paid';
  total: string;
  subtotal: string;
  discountTotal: string;
  freight: string;
  taxTotal: string;
  perceptionTotal?: string | null;
  currency: string;
  exchangeRate: string;
  paymentTerms?: string | null;
  paymentMethod?: string;
  paymentDays?: number;
  generalDiscountPct?: string | null;
  expectedDate: string | null;
  receivedDate: string | null;
  estimatedPaymentDate: string | null;
  issueDate: string;
  notes?: string | null;
  createdAt: string;
  items: POItem[];
  receptions: Reception[];
  invoices: Invoice[];
  payments: Payment[];
  totalPaid: string;
  balance: string;
  // Attachments (full rows come back inside the POD payload)
  attachments?: Attachment[];
  // Delivery metadata (channel snapshot results, see notifier)
  emailSentAt?: string | null;
  whatsappSentAt?: string | null;
  sendError?: string | null;
}

interface PriceSuggestion {
  productId: string;
  productName: string;
  oldCostPrice: string;
  newCostPrice: string;
  currentSalePrice: string;
  suggestedSalePrice: string;
}

interface CashRegister {
  id: string;
  name: string;
  type: string;
  bankName?: string | null;
}

const flowSteps: { id: POStatus; label: string; icon: string }[] = [
  { id: 'draft', label: 'Borrador', icon: '📝' },
  { id: 'sent', label: 'Enviada', icon: '📨' },
  { id: 'partial_received', label: 'Entregas parciales', icon: '📥' },
  { id: 'received', label: 'Entregada', icon: '✅' },
  { id: 'closed', label: 'Cerrada', icon: '🔒' },
];

// Etiqueta legible para una unidad de medida.
function unitLabel(unit?: string | null): string {
  const u = (unit || 'unit').trim().toLowerCase();
  switch (u) {
    case 'unit': return '';
    case 'kg': return 'kg';
    case 'g': case 'gr': return 'g';
    case 'l': case 'lt': case 'litro': return 'l';
    case 'ml': return 'ml';
    case 'm': return 'm';
    case 'box': case 'caja': return 'caja';
    case 'pack': return 'pack';
    default: return u;
  }
}

const DETAIL_PAYMENT_METHOD: Record<string, string> = {
  efectivo: 'Efectivo', transferencia: 'Transferencia', cheque: 'Cheque', tarjeta: 'Tarjeta', otro: 'Otro',
};

function detailPaymentCondition(po: { paymentMethod?: string; paymentDays?: number; paymentTerms?: string | null }): string {
  const method = DETAIL_PAYMENT_METHOD[po.paymentMethod || ''] || '';
  const days = Number(po.paymentDays ?? -1);
  const moment = days === 0 ? 'contra entrega'
    : days === 7 ? '1 semana'
    : days === 15 ? '15 días'
    : days === 30 ? '30 días'
    : days === 45 ? '45 días'
    : days === 60 ? '60 días'
    : days === 90 ? '90 días'
    : days > 0 ? `${days} días`
    : null;
  if (method && moment) return `${method} · ${moment}`;
  return po.paymentTerms || '—';
}

function getProgress(status: POStatus) {
  const stepOrder: POStatus[] = ['draft', 'sent', 'partial_received', 'partial', 'received', 'closed'];
  const idx = stepOrder.indexOf(status);
  if (idx < 0) return { percent: 0, current: 0, total: flowSteps.length };
  const current = stepOrder.findIndex((s) => s === status);
  const total = flowSteps.length - 1;
  return { percent: Math.min(100, Math.round((current / total) * 100)), current, total };
}

export function PurchaseOrderDetail() {
  const { notify } = useToast();
  const params = useParams({ strict: false });
  const poId = (params as any)?.id || '';

  const [po, setPo] = useState<PurchaseOrder | null>(null);
  const [registers, setRegisters] = useState<CashRegister[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState<string | null>(null);

  const [tab, setTab] = useState<'items' | 'receptions' | 'invoices' | 'payments' | 'attachments'>('items');

  // Receiving
  const [receiving, setReceiving] = useState(false);
  const [recvForm, setRecvForm] = useState({
    supplierRemitoNumber: '',
    notes: '',
    items: [] as { poItemId: string; productId: string | null; receivedQuantity: string }[],
  });
  // Invoice
  const [showInvForm, setShowInvForm] = useState(false);
  const [invForm, setInvForm] = useState({
    invoiceType: 'A',
    invoiceNumber: '',
    cai: '',
    caiDueDate: '',
    invoiceDate: new Date().toISOString().slice(0, 10),
    taxableBase: '0',
    ivaAmount: '0',
    ivaRate: '21',
    perceptionAmount: '0',
    total: '0',
  });

  // Payment
  const [showPayForm, setShowPayForm] = useState(false);
  const [payForm, setPayForm] = useState({
    amount: '',
    paymentMethod: 'transfer',
    reference: '',
    registerId: '',
    retentionIva: '0',
    retentionGanancias: '0',
    retentionSuss: '0',
    retentionIibb: '0',
    notes: '',
    checkNumber: '',
    checkBankName: '',
    checkDueDate: '',
    checkPayee: '',
  });

  const [priceSuggestions, setPriceSuggestions] = useState<PriceSuggestion[]>([]);

  // Overpayment guard (FR-SUP-005): first click on an overpay arms the inline
  // confirmation row (NOT submitting); the confirm button sends for real.
  const [overpayArmed, setOverpayArmed] = useState(false);

  // Accessible confirm dialog state for closing the PO (replaces the native
  // dialogs).
  const [pendingClose, setPendingClose] = useState(false);

  // Real attachment count for the "Adjuntos" tab badge. Seeded from the PO
  // detail payload on every load() and kept live by AttachmentsPanel's
  // onCountChange (upload/delete in the panel).
  const [attachmentCount, setAttachmentCount] = useState<number | null>(null);

  const token = localStorage.getItem('accessToken');
  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-tenant-slug': localStorage.getItem('tenantSlug') || '',
  };

  async function load() {
    if (!poId) return;
    setLoading(true);
    setError(null);
    try {
      const [poRes, regRes] = await Promise.all([
        fetch(`${API}/purchase-orders/${poId}`, { headers }),
        fetch(`${API}/cash/registers`, { headers }),
      ]);
      if (!poRes.ok) {
        const err = await poRes.json().catch(() => ({}));
        throw new Error(err.error || 'Error al cargar la orden');
      }
      const data = (await poRes.json()) as { data: PurchaseOrder };
      setPo(data.data);
      setAttachmentCount(data.data?.attachments?.length || 0);
      if (data.data?.items) {
        setRecvForm({
          supplierRemitoNumber: '',
          notes: '',
          items: data.data.items.map((i) => ({
            poItemId: i.id,
            productId: i.productId,
            receivedQuantity: String(Number(i.quantity) - Number(i.receivedQuantity || 0)),
          })),
        });
      }
      if (regRes.ok) {
        const regData = (await regRes.json()) as { data?: CashRegister[] };
        setRegisters(regData.data || []);
      }
    } catch (e: any) {
      console.error('PurchaseOrderDetail load error:', e);
      setError(e.message || 'Error al cargar la orden');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poId]);

  const canReceive = po ? ['draft', 'sent', 'partial_received', 'partial'].includes(po.status) : false;
  const canPay = po ? ['sent', 'partial_received', 'partial', 'received'].includes(po.status) : false;
  const canClose = po
    ? ['partial_received', 'partial', 'received'].includes(po.status) && po.paymentStatus === 'paid'
    : false;

  const progress = useMemo(() => {
    if (!po) return { percent: 0, current: 0, total: 0 };
    return getProgress(po.status);
  }, [po]);

  const overallReceived = useMemo(() => {
    if (!po?.items?.length) return { ordered: 0, received: 0, percent: 0 };
    const ordered = po.items.reduce((sum, i) => sum + Number(i.quantity || 0), 0);
    const received = po.items.reduce((sum, i) => sum + Number(i.receivedQuantity || 0), 0);
    return { ordered, received, percent: ordered > 0 ? Math.round((received / ordered) * 100) : 0 };
  }, [po]);

  function dominantAction() {
    if (!po) return null;
    if (po.status === 'draft') return { key: 'send', label: '📨 Enviar orden', color: 'bg-blue-600 hover:bg-blue-700' };
    if (canReceive) return { key: 'receive', label: '📦 Recibir mercadería', color: 'bg-green-600 hover:bg-green-700' };
    if (canPay) return { key: 'pay', label: '💰 Registrar pago', color: 'bg-purple-600 hover:bg-purple-700' };
    if (canClose) return { key: 'close', label: '🔒 Cerrar orden', color: 'bg-indigo-600 hover:bg-indigo-700' };
    return null;
  }

  function handleDominantAction() {
    if (!po) return;
    const action = dominantAction()?.key;
    if (action === 'send') sendPO();
    if (action === 'receive') {
      setReceiving(true);
      setTab('items');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    if (action === 'pay') {
      setShowPayForm(true);
      setOverpayArmed(false);
      setTab('payments');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    if (action === 'close') closePO();
  }

  async function sendPO() {
    setActionBusy('send');
    try {
      const res = await fetch(`${API}/purchase-orders/${poId}/send`, { method: 'POST', headers });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Error al enviar');
      }
      notify({ type: 'success', message: 'Orden enviada al proveedor' });
      await load();
    } catch (e: any) {
      notify({ type: 'error', message: 'No se pudo enviar la orden' });
    } finally {
      setActionBusy(null);
    }
  }

  // Native dialogs replaced by the accessible ConfirmModal (#2): this is an
  // opener; execution happens in executeClosePO.
  function closePO() {
    setPendingClose(true);
  }

  async function executeClosePO() {
    setActionBusy('close');
    try {
      const res = await fetch(`${API}/purchase-orders/${poId}/close`, { method: 'POST', headers });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Error al cerrar');
      }
      notify({ type: 'success', message: 'Orden cerrada' });
      await load();
    } catch (e: any) {
      notify({ type: 'error', message: 'No se pudo cerrar la orden' });
    } finally {
      setActionBusy(null);
      setPendingClose(false);
    }
  }

  function receiveAll() {
    if (!po) return;
    setRecvForm((prev) => ({
      ...prev,
      items: po.items.map((item) => {
        const pending = Number(item.quantity) - Number(item.receivedQuantity || 0);
        // La clave es el id de la linea, no el producto: una linea sin catalogar
        // no tiene producto, y dos de esas se cruzarian entre si.
        return {
          poItemId: item.id,
          productId: item.productId,
          receivedQuantity: String(Math.max(0, pending)),
        };
      }),
    }));
  }

  function updateRecvQuantity(poItemId: string, value: string) {
    setRecvForm((prev) => ({
      ...prev,
      items: prev.items.map((i) => (i.poItemId === poItemId ? { ...i, receivedQuantity: value } : i)),
    }));
  }

  async function submitReception() {
    const items = recvForm.items
      .filter((i) => Number(i.receivedQuantity) > 0)
      .map((i) => ({
        // El id de la linea identifica sin ambiguedad, incluso sin producto.
        poItemId: i.poItemId,
        productId: i.productId || null,
        receivedQuantity: Number(i.receivedQuantity),
      }));

    if (!items.length) {
      notify({ type: 'error', message: 'Ingresá al menos una cantidad recibida mayor a 0' });
      return;
    }

    setActionBusy('receive');
    try {
      const res = await fetch(`${API}/purchase-orders/${poId}/receive`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          supplierRemitoNumber: recvForm.supplierRemitoNumber || undefined,
          notes: recvForm.notes || undefined,
          receivedItems: items,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Error al recibir');
      }
      const data = (await res.json()) as { priceSuggestions?: PriceSuggestion[]; po?: { status?: string } };
      setReceiving(false);
      setPriceSuggestions(data.priceSuggestions || []);
      notify({
        type: 'success',
        message: data.po?.status === 'partial_received'
          ? 'Recepción parcial registrada'
          : 'Recepción registrada',
      });
      await load();
    } catch (e: any) {
      notify({ type: 'error', message: 'No se pudo registrar la recepción' });
    } finally {
      setActionBusy(null);
    }
  }

  async function applySuggestedPrice(productId: string, newPrice: string) {
    try {
      const res = await fetch(`${API}/products/${productId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ salePrice: newPrice }),
      });
      if (!res.ok) throw new Error('Error al actualizar precio');
      setPriceSuggestions((prev) => prev.filter((s) => s.productId !== productId));
      notify({ type: 'success', message: 'Precio de venta actualizado' });
      await load();
    } catch (e: any) {
      notify({ type: 'error', message: 'No se pudo actualizar el precio de venta' });
    }
  }

  function dismissSuggestion(productId: string) {
    setPriceSuggestions((prev) => prev.filter((s) => s.productId !== productId));
  }

  // ===== Invoice autocalc: total = taxable base + IVA (never typed by hand) =====
  const invBase = Number(invForm.taxableBase);
  const invIva = Number(invForm.ivaAmount);
  const invAutoTotal =
    Number.isFinite(invBase) && Number.isFinite(invIva) ? Math.round((invBase + invIva) * 100) / 100 : 0;
  const invTotalInvalid =
    !Number.isFinite(invBase) || !Number.isFinite(invIva) || invBase < 0 || invIva < 0 || invAutoTotal <= 0;

  async function submitInvoice() {
    if (invTotalInvalid) {
      notify({
        type: 'error',
        message: 'Revisá el total de la factura: la base imponible más el IVA deben dar un monto mayor a 0.',
      });
      return;
    }
    setActionBusy('invoice');
    try {
      const res = await fetch(`${API}/purchase-orders/${poId}/invoices`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...invForm,
          total: invAutoTotal.toFixed(2),
          caiDueDate: invForm.caiDueDate || undefined,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Error al registrar factura');
      }
      setShowInvForm(false);
      setInvForm((f) => ({ ...f, invoiceNumber: '', cai: '' }));
      notify({ type: 'success', message: 'Factura registrada' });
      await load();
    } catch (e: any) {
      notify({ type: 'error', message: 'No se pudo registrar la factura' });
    } finally {
      setActionBusy(null);
    }
  }

  async function submitPayment() {
    // Overpayment guard: first click arms the inline confirmation (NOT
    // submitting); "Confirmá el sobrepago" proceeds with the arm already set.
    if (isOverpay && !overpayArmed) {
      setOverpayArmed(true);
      return;
    }
    setActionBusy('payment');
    try {
      const res = await fetch(`${API}/purchase-orders/${poId}/pay`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payForm),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Error al registrar pago');
      }
      // Success toast derives from response fields ONLY — never from `message`
      // (raw English backend string, NFR-SUP-003). Method-neutral: no
      // payment-method enum mapping (vocab conflict, spec).
      const body = (await res.json()) as {
        fullyPaid?: boolean;
        overpayment?: number;
        paidSoFar?: number;
        poTotal?: number;
        message?: string;
      };
      const parts: string[] = ['Pago registrado'];
      if (body.fullyPaid) {
        parts.push('La orden quedó pagada.');
      }
      if (Number(body.overpayment) > 0) {
        parts.push(`Sobrepago: ${formatMoney(body.overpayment)} ${po?.currency || ''}.`);
      } else if (body.poTotal != null && body.paidSoFar != null) {
        parts.push(`Saldo restante: ${formatMoney(Number(body.poTotal) - Number(body.paidSoFar))} ${po?.currency || ''}.`);
      }
      notify({ type: 'success', message: parts.join(' ') });
      setShowPayForm(false);
      setPayForm((f) => ({
        ...f,
        amount: '',
        reference: '',
        checkNumber: '',
        checkBankName: '',
        checkDueDate: '',
        checkPayee: '',
      }));
      setOverpayArmed(false);
      await load();
    } catch (e: any) {
      notify({ type: 'error', message: 'No se pudo registrar el pago' });
    } finally {
      setActionBusy(null);
    }
  }

  const totalRetentions = useMemo(() => {
    return (
      (Number(payForm.retentionIva) || 0) +
      (Number(payForm.retentionGanancias) || 0) +
      (Number(payForm.retentionSuss) || 0) +
      (Number(payForm.retentionIibb) || 0)
    );
  }, [payForm]);

  const grossPreview = (Number(payForm.amount) || 0) + totalRetentions;

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto text-center">
        <div className="inline-block h-8 w-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-3" />
        <p className="text-gray-600 dark:text-gray-300 font-medium">Cargando orden de compra...</p>
      </div>
    );
  }

  if (error || !po) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-8 text-center">
          <p className="text-4xl mb-3">⚠️</p>
          <p className="text-gray-700 dark:text-gray-200 font-medium mb-4">{error || 'Orden no encontrada.'}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {error && (
              <button
                onClick={load}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors min-h-[44px]"
              >
                Reintentar
              </button>
            )}
            <Link
              to="/purchase-orders"
              className="inline-flex items-center justify-center px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors min-h-[44px]"
            >
              ← Volver al listado
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const dom = dominantAction();

  // Attachments badge: live state wins; falls back to the loaded payload.
  const attachCount = attachmentCount ?? po.attachments?.length ?? 0;

  // Overpayment guard — NET-ONLY: compares the net amount against po.balance.
  // NEVER grossPreview (gross = net + retentions; retentions are exempt,
  // spec FR-SUP-005 scenario "Retention payments do not false-positive").
  const payAmount = Number(payForm.amount) || 0;
  const balance = Number(po.balance);
  const isOverpay = payAmount - balance > 0.005; // half-cent tolerance (spec)
  // Clamp so amount = balance + 0.004 never renders a negative "Saldo restante".
  const restante = Math.max(0, balance - payAmount);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto">
      {/* Back link */}
      <Link
        to="/purchase-orders"
        className="inline-flex items-center text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 mb-4 min-h-[44px]"
      >
        ← Volver al listado
      </Link>

      {/* Header / Status hero */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 sm:p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">{po.poNumber}</h1>
            <p className="text-gray-600 dark:text-gray-300 mt-1">
              {po.supplierName} • {formatDate(po.issueDate)}
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm">
              <span className="text-gray-600 dark:text-gray-300">💳 {detailPaymentCondition(po)}</span>
              {po.estimatedPaymentDate && (
                <span className="text-gray-500 dark:text-gray-400">
                  ⏳ Pago estimado: <span className="font-semibold">{formatDate(po.estimatedPaymentDate)}</span>
                </span>
              )}
            </div>
            {(po.emailSentAt || po.whatsappSentAt || po.sendError) && (
              <div className="flex flex-wrap gap-2 mt-2">
                {po.emailSentAt && (
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300">
                    📧 Email enviado
                  </span>
                )}
                {po.whatsappSentAt && (
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300">
                    💬 WhatsApp enviado
                  </span>
                )}
                {po.sendError && (
                  <span
                    title={po.sendError}
                    className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300"
                  >
                    ⚠️ No se pudo enviar
                  </span>
                )}
              </div>
            )}
            {po.notes && <p className="text-sm text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 mt-2">{po.notes}</p>}
          </div>
          <div className="flex flex-wrap gap-2">
            <span
              className={`px-3 py-1.5 rounded-full text-sm font-semibold ${
                statusColors[po.status] || 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200'
              }`}
            >
              {statusLabels[po.status] || po.status}
            </span>
            <span
              className={`px-3 py-1.5 rounded-full text-sm font-semibold ${
                paymentStatusColors[po.paymentStatus] || 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200'
              }`}
            >
              {paymentStatusLabels[po.paymentStatus] || po.paymentStatus}
            </span>
          </div>
        </div>

        {/* Flow visual */}
        <div className="mb-6">
          <div className="flex flex-wrap items-center gap-1 text-xs mb-2">
            {flowSteps.map((step, idx, arr) => {
              const stepOrder: POStatus[] = ['draft', 'sent', 'partial_received', 'partial', 'received', 'closed'];
              const currentIdx = stepOrder.indexOf(po.status);
              const stepIdx = stepOrder.indexOf(step.id);
              const isDone = stepIdx <= currentIdx;
              const isCurrent = step.id === po.status || (step.id === 'partial_received' && po.status === 'partial');
              const isCancelled = po.status === 'cancelled';
              return (
                <div key={step.id} className="flex items-center">
                  <span
                    className={`px-2.5 py-1 rounded-full font-medium ${
                      isCancelled
                        ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
                        : isCurrent
                        ? 'bg-blue-600 text-white'
                        : isDone
                        ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-400'
                    }`}
                  >
                    {step.icon} {step.label}
                  </span>
                  {idx < arr.length - 1 && <span className="text-gray-300 mx-1">→</span>}
                </div>
              );
            })}
            {po.status === 'cancelled' && (
              <span className="ml-2 px-2.5 py-1 rounded-full bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-xs font-bold">
                ✕ Cancelada
              </span>
            )}
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-blue-600 h-2.5 rounded-full transition-all"
              style={{ width: `${po.status === 'cancelled' ? 0 : progress.percent}%` }}
            />
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 uppercase">Total</p>
            <p className="text-lg sm:text-xl font-bold text-gray-900">
              {formatMoney(po.total)} {po.currency}
            </p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 uppercase">Saldo</p>
            <p className={`text-lg sm:text-xl font-bold ${Number(po.balance) > 0 ? 'text-red-600' : 'text-green-600 dark:text-green-400'}`}>
              {formatMoney(po.balance)} {po.currency}
            </p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 uppercase">Pagado</p>
            <p className="text-lg sm:text-xl font-bold text-green-600 dark:text-green-400">
              {formatMoney(po.totalPaid)} {po.currency}
            </p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 uppercase">Fecha esperada</p>
            <p className="text-lg sm:text-xl font-bold text-gray-900">{formatDate(po.expectedDate)}</p>
          </div>
        </div>

        {/* Dominant action */}
        {dom && (
          <button
            onClick={handleDominantAction}
            disabled={!!actionBusy}
            className={`w-full ${dom.color} text-white rounded-xl px-6 py-4 font-bold text-lg shadow transition-colors disabled:opacity-70 min-h-[56px]`}
          >
            {actionBusy === dom.key ? 'Procesando...' : dom.label}
          </button>
        )}
        {po.status === 'closed' && (
          <div className="w-full bg-indigo-50 text-indigo-700 rounded-xl px-6 py-4 font-bold text-lg text-center min-h-[56px] flex items-center justify-center">
            🔒 Orden cerrada
          </div>
        )}
        {po.status === 'cancelled' && (
          <div className="w-full bg-red-50 text-red-700 dark:text-red-300 rounded-xl px-6 py-4 font-bold text-lg text-center min-h-[56px] flex items-center justify-center">
            ✕ Orden cancelada
          </div>
        )}
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 p-4 rounded-lg mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <span className="text-sm font-medium">⚠️ {error}</span>
          <button
            onClick={load}
            className="px-4 py-2 bg-red-700 text-white rounded-lg text-sm font-medium hover:bg-red-800 transition-colors min-h-[44px]"
          >
            Reintentar
          </button>
        </div>
      )}

      {/* Receiving form */}
      {receiving && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 sm:p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold">📦 Recepción de Mercadería</h2>
            <button
              onClick={receiveAll}
              type="button"
              className="text-sm text-blue-600 dark:text-blue-400 font-semibold hover:underline min-h-[44px]"
            >
              Recibir todo
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">N° Remito del proveedor</label>
              <input
                type="text"
                placeholder="ej: R-00123-2026"
                value={recvForm.supplierRemitoNumber}
                onChange={(e) => setRecvForm((f) => ({ ...f, supplierRemitoNumber: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">Notas</label>
              <input
                type="text"
                value={recvForm.notes}
                onChange={(e) => setRecvForm((f) => ({ ...f, notes: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base"
              />
            </div>
          </div>

          {/* Mobile receiving cards */}
          <div className="space-y-3 lg:hidden mb-4">
            {po.items.map((item) => {
              const pending = Number(item.quantity) - Number(item.receivedQuantity || 0);
              const recv = recvForm.items.find((r) => r.poItemId === item.id);
              const value = recv ? recv.receivedQuantity : '0';
              const pct = Number(item.quantity) > 0 ? Math.round((Number(item.receivedQuantity || 0) / Number(item.quantity)) * 100) : 0;
              return (
                <div key={item.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-semibold text-gray-900">{item.productName}</p>
                      {item.productSku && <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">SKU: {item.productSku}</p>}
                    </div>
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                      {item.receivedQuantity || 0}/{item.quantity} <span className="text-xs text-gray-400">{unitLabel(item.productPurchaseUnit)}</span>
                    </span>
                  </div>
                  {item.productPurchaseUnit && item.productUnit && item.productPurchaseUnit !== item.productUnit && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Se vende en {unitLabel(item.productUnit)} · la compra en {unitLabel(item.productPurchaseUnit)} se convierte al recibir
                    </p>
                  )}
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                    <div className="bg-green-500 h-2 rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Pendiente: {pending} {unitLabel(item.productPurchaseUnit)}</span>
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        min="0"
                        value={value}
                        onChange={(e) => updateRecvQuantity(item.id, e.target.value)}
                        className="w-28 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base text-center"
                      />
                      <span className="text-xs text-gray-400 dark:text-gray-500">{unitLabel(item.productPurchaseUnit)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop receiving table */}
          <div className="hidden lg:block overflow-x-auto mb-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-gray-200 dark:border-gray-700">
                  <th className="text-left py-2 px-2">Producto</th>
                  <th className="text-center py-2 px-2">Pedido</th>
                  <th className="text-center py-2 px-2">Recibido</th>
                  <th className="text-center py-2 px-2">Pendiente</th>
                  <th className="text-center py-2 px-2">A recibir ahora</th>
                </tr>
              </thead>
              <tbody>
                {po.items.map((item) => {
                  const pending = Number(item.quantity) - Number(item.receivedQuantity || 0);
                  const recv = recvForm.items.find((r) => r.poItemId === item.id);
                  return (
                    <tr key={item.id} className="border-b border-gray-100">
                      <td className="py-2 px-2">
                        {item.productName}
                        {item.productSku && <span className="text-gray-400 dark:text-gray-400 text-xs ml-1">({item.productSku})</span>}
                      </td>
                      <td className="text-center py-2 px-2">{item.quantity} <span className="text-xs text-gray-400 dark:text-gray-500">{unitLabel(item.productPurchaseUnit)}</span></td>
                      <td className="text-center py-2 px-2">{item.receivedQuantity || '0'} <span className="text-xs text-gray-400 dark:text-gray-500">{unitLabel(item.productPurchaseUnit)}</span></td>
                      <td className={`text-center py-2 px-2 font-semibold ${pending > 0 ? 'text-red-600' : 'text-green-600 dark:text-green-400'}`}>
                        {pending}
                      </td>
                      <td className="text-center py-2 px-2">
                        <input
                          type="number"
                          min="0"
                          value={recv ? recv.receivedQuantity : '0'}
                          onChange={(e) => updateRecvQuantity(item.id, e.target.value)}
                          className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base text-center"
                        /> <span className="text-xs text-gray-400 dark:text-gray-500">{unitLabel(item.productPurchaseUnit)}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <AttachmentPicker
                poId={po.id}
                category="reception"
                label="📎 Adjuntar remito"
                onError={setError}
              />
            </div>
            <button
              type="button"
              onClick={() => setReceiving(false)}
              className="flex-1 px-4 py-3 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 rounded-lg font-semibold hover:bg-gray-200 transition-colors min-h-[48px]"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={submitReception}
              disabled={!!actionBusy}
              className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-70 min-h-[48px]"
            >
              {actionBusy === 'receive' ? 'Procesando...' : 'Confirmar Recepción'}
            </button>
          </div>
        </div>
      )}

      {/* Invoice form */}
      {showInvForm && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 sm:p-6 mb-6">
          <h2 className="text-lg font-bold mb-4">🧾 Registrar Factura de Compra</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">Tipo</label>
              <select
                value={invForm.invoiceType}
                onChange={(e) => setInvForm((f) => ({ ...f, invoiceType: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base"
              >
                <option value="A">A</option>
                <option value="B">B</option>
                <option value="C">C</option>
                <option value="M">M</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">N° Factura</label>
              <input
                type="text"
                placeholder="0001-00012345"
                value={invForm.invoiceNumber}
                onChange={(e) => setInvForm((f) => ({ ...f, invoiceNumber: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">CAI</label>
              <input
                type="text"
                value={invForm.cai}
                onChange={(e) => setInvForm((f) => ({ ...f, cai: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">Vto CAI</label>
              <input
                type="date"
                value={invForm.caiDueDate}
                onChange={(e) => setInvForm((f) => ({ ...f, caiDueDate: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">Fecha factura</label>
              <input
                type="date"
                value={invForm.invoiceDate}
                onChange={(e) => setInvForm((f) => ({ ...f, invoiceDate: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">Base imponible</label>
              <input
                type="number"
                step="0.01"
                value={invForm.taxableBase}
                onChange={(e) => setInvForm((f) => ({ ...f, taxableBase: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">IVA ($)</label>
              <input
                type="number"
                step="0.01"
                value={invForm.ivaAmount}
                onChange={(e) => setInvForm((f) => ({ ...f, ivaAmount: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">Percepciones ($)</label>
              <input
                type="number"
                step="0.01"
                value={invForm.perceptionAmount}
                onChange={(e) => setInvForm((f) => ({ ...f, perceptionAmount: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base"
              />
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">Total factura</label>
            <input
              type="number"
              step="0.01"
              readOnly
              value={invAutoTotal.toFixed(2)}
              className="w-full sm:w-64 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base font-bold bg-gray-50 dark:bg-gray-700"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Se calcula automáticamente: base imponible + IVA.
            </p>
            {invTotalInvalid && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                Ingresá base imponible e IVA ≥ 0 para poder registrar la factura (el total debe ser mayor a 0).
              </p>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={() => setShowInvForm(false)}
              className="flex-1 px-4 py-3 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 rounded-lg font-semibold hover:bg-gray-200 transition-colors min-h-[48px]"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={submitInvoice}
              disabled={!!actionBusy || invTotalInvalid}
              className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-70 min-h-[48px]"
            >
              {actionBusy === 'invoice' ? 'Registrando...' : 'Registrar Factura'}
            </button>
          </div>
        </div>
      )}

      {/* Payment form */}
      {showPayForm && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 sm:p-6 mb-6">
          <h2 className="text-lg font-bold mb-4">💰 Registrar Pago</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">Monto neto a proveedor</label>
              <input
                type="number"
                step="0.01"
                placeholder="0,00"
                value={payForm.amount}
                onChange={(e) => {
                  setPayForm((f) => ({ ...f, amount: e.target.value }));
                  setOverpayArmed(false);
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base"
              />
              {/* Quick-fill: sets the NET balance, never grossPreview (spec) */}
              <button
                type="button"
                onClick={() => {
                  setPayForm((f) => ({ ...f, amount: String(po.balance) }));
                  setOverpayArmed(false);
                }}
                className="mt-1 text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline min-h-[44px]"
              >
                Saldo: {formatMoney(po.balance)}
              </button>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">Medio de pago</label>
              <select
                value={payForm.paymentMethod}
                onChange={(e) => setPayForm((f) => ({ ...f, paymentMethod: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base"
              >
                <option value="transfer">Transferencia</option>
                <option value="cash">Efectivo</option>
                <option value="own_check">Cheque propio</option>
                <option value="third_party_check">Cheque de terceros</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">Cuenta bancaria</label>
              <select
                value={payForm.registerId}
                onChange={(e) => setPayForm((f) => ({ ...f, registerId: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base"
              >
                <option value="">Sin vincular...</option>
                {registers
                  .filter((r) => r.type === 'bank_account')
                  .map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name} {r.bankName ? `(${r.bankName})` : ''}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {payForm.paymentMethod === 'own_check' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">N° cheque *</label>
                <input
                  type="text"
                  placeholder="00012345"
                  value={payForm.checkNumber}
                  onChange={(e) => setPayForm((f) => ({ ...f, checkNumber: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">Banco del cheque</label>
                <input
                  type="text"
                  placeholder="Banco Nación"
                  value={payForm.checkBankName}
                  onChange={(e) => setPayForm((f) => ({ ...f, checkBankName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">Vencimiento *</label>
                <input
                  type="date"
                  value={payForm.checkDueDate}
                  onChange={(e) => setPayForm((f) => ({ ...f, checkDueDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">Beneficiario</label>
                <input
                  type="text"
                  placeholder="Proveedor"
                  value={payForm.checkPayee}
                  onChange={(e) => setPayForm((f) => ({ ...f, checkPayee: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base"
                />
              </div>
              <p className="text-xs text-amber-700 sm:col-span-2 lg:col-span-4">
                💡 El cheque no descuenta del banco ahora. Se debitará a la fecha de vencimiento.
              </p>
            </div>
          )}

          <p className="font-semibold text-sm text-gray-700 dark:text-gray-200 mb-2">Retenciones (opcional)</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">IVA</label>
              <input
                type="number"
                step="0.01"
                value={payForm.retentionIva}
                onChange={(e) => {
                  setPayForm((f) => ({ ...f, retentionIva: e.target.value }));
                  setOverpayArmed(false);
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">Ganancias</label>
              <input
                type="number"
                step="0.01"
                value={payForm.retentionGanancias}
                onChange={(e) => {
                  setPayForm((f) => ({ ...f, retentionGanancias: e.target.value }));
                  setOverpayArmed(false);
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">Suss</label>
              <input
                type="number"
                step="0.01"
                value={payForm.retentionSuss}
                onChange={(e) => {
                  setPayForm((f) => ({ ...f, retentionSuss: e.target.value }));
                  setOverpayArmed(false);
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">IIBB</label>
              <input
                type="number"
                step="0.01"
                value={payForm.retentionIibb}
                onChange={(e) => {
                  setPayForm((f) => ({ ...f, retentionIibb: e.target.value }));
                  setOverpayArmed(false);
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base"
              />
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
              <div>Neto: {formatMoney(payForm.amount)}</div>
              <div>Retenciones: {formatMoney(totalRetentions)}</div>
              <div className="font-bold">Bruto: {formatMoney(grossPreview)}</div>
              {/* Resulting balance preview — green restante / red sobrepago (net) */}
              <div
                className={`font-bold ${
                  isOverpay ? 'text-red-600' : 'text-green-600 dark:text-green-400'
                }`}
              >
                {isOverpay
                  ? `Sobrepago: ${formatMoney(payAmount - balance)} ${po.currency}`
                  : `Saldo restante: ${formatMoney(restante)} ${po.currency}`}
              </div>
            </div>
            {/* Informational hint only — the cash registers are ARS-only, so a
                non-ARS PO is settled at the exchange rate of the OC. Never
                blocks or changes the amount submitted (still in PO currency). */}
            {po.currency && po.currency !== 'ARS' && Number(po.exchangeRate || 0) > 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                Bruto ≈ {formatMoney(grossPreview * Number(po.exchangeRate))} ARS a la cotización de la OC
                ({po.exchangeRate} por {po.currency})
              </p>
            )}
          </div>

          {/* Overpayment warning — inline (POS.tsx:734-739 idiom), no modal */}
          {isOverpay && !overpayArmed && (
            <div className="mb-4 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-lg p-3 text-sm text-amber-800 dark:text-amber-200 flex items-center gap-2">
              <span>⚠️</span>
              <span>
                El monto supera el saldo. Vas a dejar un sobrepago de {formatMoney(payAmount - balance)}{' '}
                {po.currency}.
              </span>
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">Referencia (N° op.)</label>
            <input
              type="text"
              value={payForm.reference}
              onChange={(e) => setPayForm((f) => ({ ...f, reference: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base"
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <AttachmentPicker
                poId={po.id}
                category="payment"
                label="📎 Adjuntar comprobante"
                onError={setError}
              />
            </div>
            <button
              type="button"
              onClick={() => setShowPayForm(false)}
              className="flex-1 px-4 py-3 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 rounded-lg font-semibold hover:bg-gray-200 transition-colors min-h-[48px]"
            >
              Cancelar
            </button>
            {overpayArmed ? (
              <>
                <p className="flex-1 self-center text-sm font-semibold text-gray-700 dark:text-gray-200">
                  ¿Querés confirmar el sobrepago?
                </p>
                <button
                  type="button"
                  onClick={() => setOverpayArmed(false)}
                  className="flex-1 px-4 py-3 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 rounded-lg font-semibold hover:bg-gray-200 transition-colors min-h-[48px]"
                >
                  ✕ Volver
                </button>
                <button
                  type="button"
                  onClick={submitPayment}
                  disabled={!!actionBusy}
                  className="flex-1 px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-70 min-h-[48px]"
                >
                  {actionBusy === 'payment' ? 'Registrando...' : '✓ Confirmá el sobrepago'}
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={submitPayment}
                disabled={!!actionBusy}
                className="flex-1 px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-70 min-h-[48px]"
              >
                {actionBusy === 'payment' ? 'Registrando...' : 'Confirmar Pago'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Price suggestions */}
      {priceSuggestions.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 sm:p-6 mb-6">
          <h3 className="text-lg font-bold text-amber-800 mb-2">💡 Precios de venta sugeridos</h3>
          <p className="text-sm text-amber-700 mb-4">
            El costo de los productos cambió al recibir mercadería. Te sugerimos actualizar los precios de venta
            manteniendo el margen actual.
          </p>
          <div className="space-y-3">
            {priceSuggestions.map((s) => (
              <div
                key={s.productId}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white dark:bg-gray-800 border border-amber-200 rounded-lg p-4"
              >
                <div>
                  <p className="font-semibold text-gray-900">{s.productName}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">
                    Costo: {formatMoney(s.oldCostPrice)} → {formatMoney(s.newCostPrice)} | Venta actual:{' '}
                    {formatMoney(s.currentSalePrice)} | Sugerido:{' '}
                    <span className="text-green-600 dark:text-green-400 font-bold">{formatMoney(s.suggestedSalePrice)}</span>
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => applySuggestedPrice(s.productId, s.suggestedSalePrice)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors min-h-[44px]"
                  >
                    ✓ Aplicar
                  </button>
                  <button
                    onClick={() => dismissSuggestion(s.productId)}
                    className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-semibold hover:bg-gray-200 transition-colors min-h-[44px]"
                  >
                    ✕ Ignorar
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow mb-6 overflow-hidden">
        <div className="flex overflow-x-auto border-b border-gray-200 dark:border-gray-700">
          {[
            { id: 'items', label: '📦 Items', count: po.items?.length || 0 },
            { id: 'receptions', label: '📥 Recepciones', count: po.receptions?.length || 0 },
            { id: 'invoices', label: '🧾 Facturas', count: po.invoices?.length || 0 },
            { id: 'payments', label: '💰 Pagos', count: po.payments?.length || 0 },
            { id: 'attachments', label: '📎 Adjuntos', count: attachCount },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id as typeof tab)}
              className={`flex-shrink-0 px-4 py-3 text-sm font-semibold transition-colors min-h-[48px] ${
                tab === t.id
                  ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 bg-blue-50 dark:bg-blue-900/30'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {t.label} {t.count > 0 && <span className="ml-1 text-gray-400 dark:text-gray-400">({t.count})</span>}
            </button>
          ))}
        </div>

        <div className="p-4 sm:p-6">
          {/* Items tab */}
          {tab === 'items' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-800 dark:text-gray-100">Productos</h3>
                {canReceive && !receiving && (
                  <button
                    onClick={() => setReceiving(true)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors min-h-[44px]"
                  >
                    📦 Recibir
                  </button>
                )}
              </div>

              {/* Overall progress */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4">
                <div className="flex justify-between text-sm mb-2">
                  <span className="font-semibold text-gray-700 dark:text-gray-200">Recepción general</span>
                  <span className="font-bold text-gray-900">
                    {overallReceived.received} de {overallReceived.ordered} ({overallReceived.percent}%)
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-green-500 h-3 rounded-full transition-all"
                    style={{ width: `${overallReceived.percent}%` }}
                  />
                </div>
              </div>

              {/* Mobile item cards */}
              <div className="space-y-3 lg:hidden">
                {po.items.map((item) => {
                  const pct = Number(item.quantity) > 0 ? Math.round((Number(item.receivedQuantity || 0) / Number(item.quantity)) * 100) : 0;
                  return (
                    <div key={item.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                      <p className="font-semibold text-gray-900">{item.productName}</p>
                          {item.productSku && <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">SKU: {item.productSku}</p>}
                        </div>
                        <span className="text-sm font-bold text-gray-900">{formatMoney(item.total)}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 dark:text-gray-300 mb-3">
                        <div>Cantidad: {item.quantity} <span className="text-xs text-gray-400 dark:text-gray-500">{unitLabel(item.productPurchaseUnit)}</span></div>
                        <div>Costo: {formatMoney(item.unitCost)}</div>
                        <div>Desc.: {item.discountPct || '0'}%</div>
                        <div>IVA: {item.taxRate || '21'}%</div>
                      </div>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-gray-600 dark:text-gray-300">Recibido</span>
                        <span className="font-semibold">
                          {item.receivedQuantity || 0}/{item.quantity}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop items table */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-gray-200 dark:border-gray-700">
                      <th className="text-left py-2 px-2">Producto</th>
                      <th className="text-center py-2 px-2">Cant.</th>
                      <th className="text-right py-2 px-2">Costo unit.</th>
                      <th className="text-center py-2 px-2">Desc. %</th>
                      <th className="text-center py-2 px-2">IVA %</th>
                      <th className="text-right py-2 px-2">Subtotal</th>
                      <th className="text-right py-2 px-2">IVA</th>
                      <th className="text-right py-2 px-2">Total</th>
                      <th className="text-center py-2 px-2">Recibido</th>
                    </tr>
                  </thead>
                  <tbody>
                    {po.items.map((item) => {
                      const pct = Number(item.quantity) > 0 ? Math.round((Number(item.receivedQuantity || 0) / Number(item.quantity)) * 100) : 0;
                      return (
                        <tr key={item.id} className="border-b border-gray-100">
                          <td className="py-2 px-2">
                            {item.productName}
                            {item.productSku && <span className="text-gray-400 dark:text-gray-400 text-xs ml-1">({item.productSku})</span>}
                          </td>
                          <td className="text-center py-2 px-2">{item.quantity}</td>
                          <td className="text-right py-2 px-2">{formatMoney(item.unitCost)}</td>
                          <td className="text-center py-2 px-2">{item.discountPct || '0'}%</td>
                          <td className="text-center py-2 px-2">{item.taxRate || '21'}%</td>
                          <td className="text-right py-2 px-2">{formatMoney(item.subtotal)}</td>
                          <td className="text-right py-2 px-2 text-red-500 dark:text-red-400">{formatMoney(item.taxAmount)}</td>
                          <td className="text-right py-2 px-2 font-bold">{formatMoney(item.total)}</td>
                          <td className="text-center py-2 px-2">
                            <span
                              className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${
                                pct >= 100
                                  ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                                  : pct > 0
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
                              }`}
                            >
                              {item.receivedQuantity || 0}/{item.quantity}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mt-4">
                <div className="grid grid-cols-2 gap-1 text-sm max-w-sm ml-auto">
                  <span className="text-gray-600 dark:text-gray-300">Subtotal</span>
                  <span className="text-right">{formatMoney(po.subtotal)}</span>
                  <span className="text-red-600">Descuentos</span>
                  <span className="text-right text-red-600">-{formatMoney(po.discountTotal)}</span>
                  <span className="text-red-500 dark:text-red-400">IVA</span>
                  <span className="text-right text-red-500 dark:text-red-400">+{formatMoney(po.taxTotal)}</span>
                  <span className="text-gray-600 dark:text-gray-300">Flete</span>
                  <span className="text-right">+{formatMoney(po.freight)}</span>
                  <span className="font-bold border-t border-gray-200 dark:border-gray-700 pt-1 mt-1">TOTAL</span>
                  <span className="text-right font-bold border-t border-gray-200 dark:border-gray-700 pt-1 mt-1">
                    {formatMoney(po.total)} {po.currency}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Receptions tab */}
          {tab === 'receptions' && (
            <div>
              <h3 className="font-bold text-gray-800 dark:text-gray-100 mb-4">Historial de Recepciones</h3>
              {po.receptions?.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-4xl mb-2">📭</p>
                  <p className="text-gray-600 dark:text-gray-300 font-medium">No hay recepciones registradas.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {po.receptions.map((r) => (
                    <div key={r.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div>
                        <p className="font-semibold text-gray-900">{r.receptionNumber}</p>
                        {r.supplierRemitoNumber && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">Remito proveedor: {r.supplierRemitoNumber}</p>
                        )}
                        {r.notes && <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 mt-1">{r.notes}</p>}
                      </div>
                      <span className="text-sm text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">{formatDate(r.receptionDate)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Invoices tab */}
          {tab === 'invoices' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-800 dark:text-gray-100">Facturas de Compra</h3>
                {canPay && (
                  <button
                    onClick={() => setShowInvForm(!showInvForm)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors min-h-[44px]"
                  >
                    {showInvForm ? 'Cancelar' : '+ Registrar Factura'}
                  </button>
                )}
              </div>
              {po.invoices?.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-4xl mb-2">🧾</p>
                  <p className="text-gray-600 dark:text-gray-300 font-medium">No hay facturas registradas.</p>
                </div>
              ) : (
                <div className="space-y-3 lg:hidden">
                  {po.invoices.map((inv) => (
                    <div key={inv.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded text-xs font-bold">
                            {inv.invoiceType}
                          </span>
                          <p className="font-semibold text-gray-900 mt-1">{inv.invoiceNumber}</p>
                        </div>
                        <span className="text-lg font-bold text-gray-900">{formatMoney(inv.total)}</span>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-300">
                        <p>Base: {formatMoney(inv.taxableBase)}</p>
                        <p>IVA: {formatMoney(inv.ivaAmount)}</p>
                        <p>CAI: {inv.cai || '-'}</p>
                        <p>Fecha: {formatDate(inv.invoiceDate)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {po.invoices && po.invoices.length > 0 && (
                <div className="hidden lg:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-gray-200 dark:border-gray-700">
                        <th className="text-left py-2 px-2">Tipo</th>
                        <th className="text-left py-2 px-2">N°</th>
                        <th className="text-left py-2 px-2">CAI</th>
                        <th className="text-left py-2 px-2">Fecha</th>
                        <th className="text-right py-2 px-2">Base</th>
                        <th className="text-right py-2 px-2">IVA</th>
                        <th className="text-right py-2 px-2">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {po.invoices.map((inv) => (
                        <tr key={inv.id} className="border-b border-gray-100">
                          <td className="py-2 px-2">
                            <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded text-xs font-bold">
                              {inv.invoiceType}
                            </span>
                          </td>
                          <td className="py-2 px-2 font-mono">{inv.invoiceNumber}</td>
                          <td className="py-2 px-2 text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 text-xs">{inv.cai || '-'}</td>
                          <td className="py-2 px-2">{formatDate(inv.invoiceDate)}</td>
                          <td className="text-right py-2 px-2">{formatMoney(inv.taxableBase)}</td>
                          <td className="text-right py-2 px-2 text-red-500 dark:text-red-400">{formatMoney(inv.ivaAmount)}</td>
                          <td className="text-right py-2 px-2 font-bold">{formatMoney(inv.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Payments tab */}
          {tab === 'payments' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-800 dark:text-gray-100">Historial de Pagos</h3>
                {canPay && (
                  <button
                    onClick={() => {
                      setShowPayForm(!showPayForm);
                      setOverpayArmed(false);
                    }}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700 transition-colors min-h-[44px]"
                  >
                    {showPayForm ? 'Cancelar' : '+ Registrar Pago'}
                  </button>
                )}
              </div>
              {po.payments?.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-4xl mb-2">💰</p>
                  <p className="text-gray-600 dark:text-gray-300 font-medium">No hay pagos registrados.</p>
                </div>
              ) : (
                <>
                  <div className="space-y-3 lg:hidden">
                    {po.payments.map((p) => (
                      <div key={p.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <p className="font-semibold text-gray-900">{p.paymentMethod}</p>
                          <span className="text-lg font-bold text-gray-900">{formatMoney(p.grossAmount)}</span>
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-300">
                          <p>Neto: {formatMoney(p.amount)}</p>
                          {Number(p.totalRetentions) > 0 && <p>Retenciones: -{formatMoney(p.totalRetentions)}</p>}
                          <p>Ref.: {p.reference || '-'}</p>
                          <p>Fecha: {formatDate(p.paymentDate)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="hidden lg:block overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b-2 border-gray-200 dark:border-gray-700">
                          <th className="text-left py-2 px-2">Fecha</th>
                          <th className="text-left py-2 px-2">Medio</th>
                          <th className="text-left py-2 px-2">Ref.</th>
                          <th className="text-right py-2 px-2">Neto</th>
                          <th className="text-right py-2 px-2">Retenc.</th>
                          <th className="text-right py-2 px-2">Bruto</th>
                        </tr>
                      </thead>
                      <tbody>
                        {po.payments.map((p) => (
                          <tr key={p.id} className="border-b border-gray-100">
                            <td className="py-2 px-2">{formatDate(p.paymentDate)}</td>
                            <td className="py-2 px-2">{p.paymentMethod}</td>
                            <td className="py-2 px-2 text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 text-xs">{p.reference || '-'}</td>
                            <td className="text-right py-2 px-2">{formatMoney(p.amount)}</td>
                            <td className="text-right py-2 px-2 text-amber-600">
                              {Number(p.totalRetentions) > 0 ? `-${formatMoney(p.totalRetentions)}` : '-'}
                            </td>
                            <td className="text-right py-2 px-2 font-bold">{formatMoney(p.grossAmount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mt-4">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                      <div>
                        Total pagado: <strong>{formatMoney(po.totalPaid)}</strong>
                      </div>
                      <div>
                        Saldo:{' '}
                        <strong className={Number(po.balance) > 0 ? 'text-red-600' : 'text-green-600 dark:text-green-400'}>
                          {formatMoney(po.balance)}
                        </strong>
                      </div>
                      <div>
                        Total OC: <strong>{formatMoney(po.total)}</strong>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          {tab === 'attachments' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-800 dark:text-gray-100">Documentación de la orden</h3>
              </div>
              <AttachmentsPanel poId={po.id} onError={setError} onCountChange={setAttachmentCount} />
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        open={pendingClose}
        title="Cerrar orden de compra"
        message="¿Cerrar esta orden con lo recibido hasta ahora?"
        confirmLabel="Sí, cerrar la orden"
        busy={!!actionBusy}
        onCancel={() => setPendingClose(false)}
        onConfirm={() => {
          void executeClosePO();
        }}
      />
    </div>
  );
}
